// @ts-check

import { updateToken } from '@/api/oauth'
import {
  LOCAL_STORAGE_KEY_ACCESS_TOKEN,
  LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME,
  LOCAL_STORAGE_KEY_SESSION_IDENTIFIER
} from '@/core/constants'
import { store } from '@/store'
import { useCompanyStore } from '@/store/company'
import axios from 'axios'

let pendingRequestCount = 0
/** @type {NodeJS.Timer} */
let updateTokenInterval

// We do not want to check for new token while another request is pending since
// that could cause a new token update while the other request is ongoing which
// will lead the token to be old on the initial request which causes 401 unauthorized
const checkTokenUpdate = () => {
  clearInterval(updateTokenInterval)

  const sessionExpiration = localStorage.getItem(
    LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME
  )

  if (!sessionExpiration) {
    return
  }

  const currentTime = Math.floor(new Date().getTime() / 1000)

  const expirationTime = Math.floor(
    new Date(sessionExpiration).getTime() / 1000
  )

  const secondsUntilExpiration = expirationTime - currentTime
  const expirationTreshold = 900
  if (
    secondsUntilExpiration <= expirationTreshold &&
    secondsUntilExpiration > 300 &&
    pendingRequestCount === 0
  ) {
    // If there is no other request pending we update the token
    updateToken()
      .catch(() => {
        // Ignore this as updateToken handles logging out the user if needed
      })
      .finally(() => {
        clearInterval(updateTokenInterval)
      })
  } else if (
    secondsUntilExpiration <= expirationTreshold &&
    secondsUntilExpiration > 300 &&
    pendingRequestCount !== 0
  ) {
    // If there is other request pending we set an interval to update the token
    // when there is no other requests pending
    updateTokenInterval = setInterval(() => {
      checkTokenUpdate()
    }, 5000 * 5)
  }
}

export const requestResolved = (config) => {
  const configured = config
  const accessToken = localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN)
  if (accessToken) {
    configured.headers.Authorization = `Bearer ${accessToken}`
  }

  const { company } = useCompanyStore()

  if (company?.id) {
    configured.headers['Company-Id'] = company.id
  }

  const sessionIdentifier = localStorage.getItem(
    LOCAL_STORAGE_KEY_SESSION_IDENTIFIER
  )
  if (sessionIdentifier) {
    configured.headers['Session-Identifier'] = sessionIdentifier
  }

  pendingRequestCount += 1
  return configured
}

export const requestRejected = () => {
  pendingRequestCount -= 1
}

/**
 * @param {import('axios').AxiosResponse} response
 */
export const responseResolved = (response) => {
  // Check if we need to and can update the user token
  if (!store.state.mobileData.isMobileWrapperUser) {
    checkTokenUpdate()
  }
  pendingRequestCount -= 1

  return response.data
}

/**
 * @param {import('axios').AxiosError} error
 */
export const responseRejected = (error) => {
  const sessionExpiration = localStorage.getItem(
    LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME
  )
  pendingRequestCount -= 1

  if (
    error.config &&
    error.response &&
    error.response.status === 401 &&
    !store.state.mobileData.isMobileWrapperUser &&
    sessionExpiration
  ) {
    // If unauthorized (401), try to update access token and retry request
    return updateToken()
      .then(() => {
        const config = requestResolved(error.config)
        return axios(config).then((response) => response.data)
      })
      .catch(() => {
        // If there's an error, return the original error
        // updateToken handles logging out the user if needed
        throw error
      })
  }

  return Promise.reject(error)
}
